/**
 * LaunchDashboard — the tenant dashboard on the launch home (the console's
 * former Build Hub, which was its index route). What it renders is the setup
 * wizard (as the mandatory gate, then as the dismissable first-run journey) and
 * the Issue Lifecycle pipeline. The stat-tile grid that used to sit between
 * them is gone: the hub diagram directly above already names every destination,
 * so the tiles restated the navigation the page had just given.
 *
 * It renders on the bare {slug}.systemdox.com host and every navigation target
 * is a plain anchor with an absolute URL — a router path would dead-end on this
 * host's catch-all. The wizard's Docs destinations live on this same launch
 * host; its pillar/Settings destinations are Build hosts on another origin. A
 * selected project rides along as the /p/{projectSlug} prefix the console's
 * ProjectPathResolver consumes on arrival.
 *
 * Deliberately a separate file from LaunchPage: the workspace context this
 * needs (selected repo, GitHub installations) mounts here, and the
 * LaunchPage source guards (LaunchPage.test.ts) pin the page itself to hub
 * duties. All API calls carry the auth token, so they work from this origin;
 * each has a fallback, so missing grants degrade to an empty/absent signal
 * rather than a broken gate.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  GitPullRequest, GitMerge, ClipboardList, ArrowRight,
  Circle, CheckCircle2, ExternalLink, Rocket,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { PROJECT_PATH_PREFIX } from '@systemdox/types';
import { githubApi, type PlanUsage, type Repo } from '@/services/githubApi';
import { specsApi } from '@/services/specsApi';
import { workflowApi, type LifecycleData } from '@/services/workflowApi';
import { WorkspaceProvider, useWorkspace } from '@/contexts/WorkspaceContext';
import { useTenant } from '@/contexts/TenantContext';
import { useAuthContext } from '@/contexts/AuthContext';
import { SetupWizard } from '@/components/onboarding/SetupWizard';
import { PlanBadge } from '@/components/github/PlanBadge';
import { captureAppUrl, launchUrl } from '@/utils/subdomain';

/**
 * Per user+tenant "onboarding is done" latch. Set the first time a workspace is
 * seen fully onboarded, then read to keep an established workspace out of the
 * mandatory wizard forever — even if a later read comes back empty (a transient
 * error, or the last repo was disconnected). Never blocking: storage failures
 * just mean the latch doesn't persist this session.
 */
function onboardingLatchKey(userId: string, tenantId: string): string {
  return `systemdox.onboarding.done.${userId}.${tenantId}`;
}

/**
 * Per user+tenant "first-run journey is over" latch. Set when the journey card
 * is dismissed — explicitly, or automatically once capture + publish are both
 * done — so it never resurfaces. Same never-blocking posture as the
 * onboarding latch.
 */
function journeyLatchKey(userId: string, tenantId: string): string {
  return `systemdox.journey.done.${userId}.${tenantId}`;
}

const LIFECYCLE_STAGES = [
  { label: 'Pending', key: 'pending' as const, color: 'text-amber-500 fill-amber-500' },
  { label: 'In Progress', key: 'open_prs' as const, color: 'text-blue-500 fill-blue-500' },
  { label: 'Merged (7d)', key: 'merged_last_7d' as const, color: 'text-emerald-500 fill-emerald-500' },
  { label: 'Closed', key: 'closed' as const, color: 'text-slate-400 fill-slate-400' },
];

interface LaunchDashboardProps {
  /** Tenant URL slug — null (slug-less workspace) disables Build links. */
  slug: string | null;
  /** Selected project's slug, carried onto Build links as /p/{slug}. */
  projectSlug: string | null;
}

function DashboardInner({ slug, projectSlug }: LaunchDashboardProps) {
  const { selectedRepo, installations, loading: workspaceLoading } = useWorkspace();
  const { tenant } = useTenant();
  const { user } = useAuthContext();
  // Every repo the App can see — the wizard's inline picker needs the
  // unconnected ones too, so this is no longer pre-filtered to connected.
  const [repos, setRepos] = useState<Repo[]>([]);
  const [reposLoadOk, setReposLoadOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [onboardingLatched, setOnboardingLatched] = useState(false);
  const [journeyLatched, setJourneyLatched] = useState(false);
  const [reqCount, setReqCount] = useState<number>(0);
  const [lifecycle, setLifecycle] = useState<LifecycleData | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(true);
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);

  // Only what the wizard and the plan badge consume: the repo list (gate +
  // inline picker), the requirement count (a journey step), and plan usage. The
  // doc/tech/guardrail/design reads went with the stat tiles that displayed
  // them — the wizard fetches its own publish/roles signals.
  useEffect(() => {
    Promise.all([
      // Track whether the repo read SUCCEEDED — the onboarding gate fails open
      // on a failed read, so a caught-to-empty must be distinguishable here.
      githubApi.listRepos().then((list) => ({ list, ok: true })).catch(() => ({ list: [] as Repo[], ok: false })),
      specsApi.listSpecs({ docType: 'requirement' }).catch(() => []),
      githubApi.getPlanUsage().catch(() => null),
    ]).then(([repoResult, requirementList, usage]) => {
      setRepos(repoResult.list);
      setReposLoadOk(repoResult.ok);
      setReqCount(requirementList.length);
      setPlanUsage(usage);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLifecycleLoading(true);
    workflowApi.getLifecycle(selectedRepo || undefined)
      .then(setLifecycle)
      .catch(() => setLifecycle(null))
      .finally(() => setLifecycleLoading(false));
  }, [selectedRepo]);

  // The whole app lives on this root host now, so every destination is a
  // same-origin URL (launchUrl) — the dashboard stays router-free (a router
  // Link would dead-end on the catch-all). Console pillars (Plan/Execute/
  // Validate, Settings) carry a selected project as the /p/{projectSlug}
  // prefix. Both helpers survive the tile grid because onboardingHref — the
  // wizard's link resolver — is built from them.
  const buildHref = slug
    ? (path: string) =>
        launchUrl(slug, projectSlug ? `${PROJECT_PATH_PREFIX}/${projectSlug}${path}` : path)
    : null;
  // Docs and Add also live here; without a slug there is no host to target,
  // so fall back to the capture host, which forwards on.
  const docsHref = (path: string) => (slug ? launchUrl(slug, path) : captureAppUrl(path, slug));
  const onboardingHref = buildHref
    ? (path: string) => (path.startsWith('/docs') ? docsHref(path) : buildHref(path))
    : null;

  // Mandatory onboarding gate. The two required steps — connect GitHub, then
  // connect (index) a repo — must be done before the dashboard opens; until
  // then the hub shows only the SetupWizard and holds the tiles/stats/lifecycle
  // back. "Indexed" keys off a CONNECTED repo, not doc count: a code-only repo
  // indexes to zero docs, and gating on that would trap the user forever.
  const connectedRepos = repos.filter((r) => r.connected);
  const githubConnected = installations.length > 0;
  const repoConnected = connectedRepos.length > 0;
  const requiredComplete = repoConnected;
  // The workspace owner/admin is the only one who can actually connect a repo,
  // so they are the only one the mandatory wizard gates. A viewer/editor member
  // (who would otherwise be trapped, since Settings is admin-scoped) always sees
  // the dashboard.
  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';

  // Latch completion so an established workspace is never re-gated — set it the
  // first time we see a connected repo (survives a later empty/failed read).
  const latchKey = user?.userId && tenant ? onboardingLatchKey(user.userId, tenant.tenantId) : null;
  const journeyKey = user?.userId && tenant ? journeyLatchKey(user.userId, tenant.tenantId) : null;
  useEffect(() => {
    if (!latchKey) return;
    try {
      if (localStorage.getItem(latchKey) === '1') setOnboardingLatched(true);
    } catch {
      // Storage blocked — the gate falls back to the live signal.
    }
  }, [latchKey]);
  useEffect(() => {
    if (!journeyKey) return;
    try {
      if (localStorage.getItem(journeyKey) === '1') setJourneyLatched(true);
    } catch {
      // Storage blocked — the journey card just reappears next session.
    }
  }, [journeyKey]);
  const dismissJourney = useCallback(() => {
    setJourneyLatched(true);
    try {
      if (journeyKey) localStorage.setItem(journeyKey, '1');
    } catch {
      // Non-fatal — the dismissal just won't persist across reloads.
    }
  }, [journeyKey]);

  // The wizard's inline picker connected a repo: reflect it here so the gate
  // opens (and the latch sets) without a reload.
  const handleRepoConnected = useCallback((fullName: string) => {
    setRepos((prev) => prev.map((r) => (r.full_name === fullName ? { ...r, connected: true } : r)));
  }, []);
  useEffect(() => {
    if (!latchKey || !requiredComplete) return;
    setOnboardingLatched(true);
    try {
      localStorage.setItem(latchKey, '1');
    } catch {
      // Non-fatal — the latch just won't persist across reloads.
    }
  }, [latchKey, requiredComplete]);

  // Wait for the data + workspace loads before deciding, so an established
  // workspace never flashes the wizard. The tenant role loads separately (a
  // null tenant is a working degraded state — the hub still resolves its slug
  // from the hostname), so we fail OPEN on an unknown role: a fresh owner may
  // glimpse the dashboard for a frame before the wizard resolves, which beats
  // spinning forever if the tenant read never lands.
  if (loading || workspaceLoading) {
    return (
      <div className="mb-8 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/60 to-blue-50/60 p-5 dark:border-slate-800 dark:from-emerald-950/30 dark:to-blue-950/20">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Set up SystemDox</h2>
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Finish these steps to unlock your dashboard.</p>
        </div>
        <div className="flex justify-center p-8">
          <Spinner size="lg" aria-label="Loading setup" />
        </div>
      </div>
    );
  }
  // Fails open on every axis so the un-dismissable wizard can never trap anyone:
  // only a manager, only when the repo read succeeded, only a genuinely
  // un-onboarded workspace (not latched), and only where there is a host to
  // deep-link to (a slug-less workspace has onboardingHref null).
  const onboardingComplete = requiredComplete || onboardingLatched;
  if (onboardingHref && canManage && reposLoadOk && !onboardingComplete) {
    return (
      <SetupWizard
        githubConnected={githubConnected}
        repoConnected={repoConnected}
        hasRequirement={reqCount > 0}
        repos={repos}
        planUsage={planUsage}
        onRepoConnected={handleRepoConnected}
        resolveTo={onboardingHref}
      />
    );
  }

  // First-run journey: once the gate opens, the same card rides above the
  // dashboard guiding capture → publish, until finished or dismissed.
  const showJourney = Boolean(onboardingHref) && canManage && reposLoadOk && !journeyLatched;

  return (
    <div>
      {showJourney && onboardingHref && (
        <SetupWizard
          variant="journey"
          githubConnected={githubConnected}
          repoConnected={repoConnected}
          hasRequirement={reqCount > 0}
          repos={repos}
          planUsage={planUsage}
          onRepoConnected={handleRepoConnected}
          resolveTo={onboardingHref}
          onDismiss={dismissJourney}
        />
      )}
      {/* The at-limit plan pill outlived the stats header row it used to share.
          It is the only ambient signal on the tenant home that a free-tier
          workspace has hit its repo limit — the wizard's own at-limit copy
          disappears for good once the journey is dismissed — so it stands on
          its own rather than going out with the stat tiles. Self-hiding:
          nothing renders for unlimited tenants or before /plan answers. */}
      {planUsage?.at_limit && (
        <div className="mb-4 flex justify-end">
          <PlanBadge usage={planUsage} />
        </div>
      )}

      {/* Issue Lifecycle Pipeline */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Issue Lifecycle
          </h2>
          {lifecycleLoading && <Spinner size="sm" aria-label="Loading issue lifecycle" />}
        </div>

        {/* Pipeline visualization */}
        <div className="flex items-center gap-2 mb-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
          {LIFECYCLE_STAGES.map((stage, i) => (
            <div key={stage.key} className="flex items-center gap-2 flex-1">
              <div className="flex flex-col items-center gap-1 flex-1">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-full justify-center">
                  <Circle className={`w-2.5 h-2.5 ${stage.color}`} />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{stage.label}</span>
                </div>
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  {lifecycle?.counts[stage.key] ?? '—'}
                </span>
              </div>
              {i < LIFECYCLE_STAGES.length - 1 && (
                <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              )}
            </div>
          ))}
        </div>

        {/* Live activity: pending issues + open PRs */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Pending Issues */}
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <ClipboardList className="w-3.5 h-3.5" />
              Pending Issues
            </h3>
            {lifecycle?.pending_issues.length === 0 ? (
              <p className="text-sm text-slate-500 py-3">All issues have linked PRs.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {lifecycle?.pending_issues.slice(0, 8).map((issue) => (
                  <a
                    key={issue.number}
                    href={issue.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:border-amber-200 dark:hover:border-amber-800 transition-colors group"
                  >
                    <Circle className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">
                      #{issue.number} {issue.title}
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Open PRs */}
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <GitPullRequest className="w-3.5 h-3.5" />
              Open Pull Requests
            </h3>
            {lifecycle?.open_prs.length === 0 ? (
              <p className="text-sm text-slate-500 py-3">No open PRs.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {lifecycle?.open_prs.slice(0, 8).map((pr) => (
                  <a
                    key={`${pr.repo}-${pr.number}`}
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-800 transition-colors group"
                  >
                    <GitPullRequest className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">
                      #{pr.number} {pr.title}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">{pr.repo}</span>
                    <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recently merged */}
        {lifecycle && lifecycle.recently_merged.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <GitMerge className="w-3.5 h-3.5" />
              Recently Merged
            </h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {lifecycle.recently_merged.slice(0, 5).map((pr) => (
                <a
                  key={`${pr.repo}-${pr.number}`}
                  href={pr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:border-emerald-200 dark:hover:border-emerald-800 transition-colors group"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">
                    #{pr.number} {pr.title}
                  </span>
                  <span className="text-[10px] text-slate-400 shrink-0">{pr.repo}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">
                    {pr.merged_at && new Date(pr.merged_at).toLocaleDateString()}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LaunchDashboard(props: LaunchDashboardProps) {
  // The workspace context (installations, selected repo) mounts with the
  // dashboard — the rest of the launch page stays workspace-free.
  return (
    <WorkspaceProvider>
      <DashboardInner {...props} />
    </WorkspaceProvider>
  );
}
